import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    useTheme,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface"; // Assuming this helper exists
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed

// --- Configuration ---

// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     { value: "aws", label: "Amazon Web Services" },
//     { value: "azure", label: "Microsoft Azure" },
// ];
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

const analyticsServiceList = {
    gcp: [{ value: "looker", label: "Looker" }],
    aws: [{ value: "quicksight", label: "Amazon QuickSight" }],
    azure: [{ value: "powerbi", label: "Microsoft Power BI"}],
};

const editionList = {
    gcp: [
        { value: "core", label: "Looker Core" },
        { value: "standard", label: "Standard" },
        { value: "enterprise", label: "Enterprise" },
    ],
    aws: [
        { value: "standard", label: "Standard" },
        { value: "enterprise", label: "Enterprise" },
    ],
    azure: [
        { value: "pro", label: "Pro" },
        { value: "premium_per_user", label: "Premium per user" },
        { value: "premium_per_capacity", label: "Premium per capacity" },
    ],
};

const regionList = {
    gcp: [
        { value: "africa-south1", label: "africa-south1" },
        { value: "asia-east1", label: "asia-east1" },
        { value: "asia-east2", label: "asia-east2" },
        { value: "asia-northeast1", label: "asia-northeast1" },
        { value: "asia-northeast2", label: "asia-northeast2" },
        { value: "asia-northeast3", label: "asia-northeast3" },
        { value: "asia-south1", label: "asia-south1" },
        { value: "asia-south2", label: "asia-south2" },
        { value: "asia-southeast1", label: "asia-southeast1" },
        { value: "asia-southeast2", label: "asia-southeast2" },
        { value: "australia-southeast1", label: "australia-southeast1" },
        { value: "australia-southeast2", label: "australia-southeast2" },
        { value: "europe-central2", label: "europe-central2" },
        { value: "europe-north1", label: "europe-north1" },
        { value: "europe-southwest1", label: "europe-southwest1" },
        { value: "europe-west1", label: "europe-west1" },
        { value: "europe-west10", label: "europe-west10" },
        { value: "europe-west12", label: "europe-west12" },
        { value: "europe-west2", label: "europe-west2" },
        { value: "europe-west3", label: "europe-west3" },
        { value: "europe-west4", label: "europe-west4" },
        { value: "europe-west6", label: "europe-west6" },
        { value: "europe-west8", label: "europe-west8" },
        { value: "europe-west9", label: "europe-west9" },
        { value: "me-central1", label: "me-central1" },
        { value: "me-central2", label: "me-central2" },
        { value: "me-west1", label: "me-west1" },
        { value: "northamerica-northeast1", label: "northamerica-northeast1" },
        { value: "northamerica-northeast2", label: "northamerica-northeast2" },
        { value: "southamerica-east1", label: "southamerica-east1" },
        { value: "southamerica-west1", label: "southamerica-west1" },
        { value: "us-central1", label: "us-central1" },
        { value: "us-east1", label: "us-east1" },
        { value: "us-east4", label: "us-east4" },
        { value: "us-east5", label: "us-east5" },
        { value: "us-south1", label: "us-south1" },
        { value: "us-west1", label: "us-west1" },
        { value: "us-west2", label: "us-west2" },
        { value: "us-west3", label: "us-west3" },
        { value: "us-west4", label: "us-west4" },
    ],
    aws: [
        { value: "us-east-2", label: "us-east-2" },
        { value: "us-east-1", label: "us-east-1" },
        { value: "us-west-1", label: "us-west-1" },
        { value: "us-west-2", label: "us-west-2" },
        { value: "af-south-1", label: "af-south-1" },
        { value: "ap-east-1", label: "ap-east-1" },
        { value: "ap-south-2", label: "ap-south-2" },
        { value: "ap-southeast-3", label: "ap-southeast-3" },
        { value: "ap-southeast-5", label: "ap-southeast-5" },
        { value: "ap-southeast-4", label: "ap-southeast-4" },
        { value: "ap-south-1", label: "ap-south-1" },
        { value: "ap-northeast-3", label: "ap-northeast-3" },
        { value: "ap-northeast-2", label: "ap-northeast-2" },
        { value: "ap-southeast-1", label: "ap-southeast-1" },
        { value: "ap-southeast-2", label: "ap-southeast-2" },
        { value: "ap-southeast-7", label: "ap-southeast-7" },
        { value: "ap-northeast-1", label: "ap-northeast-1" },
        { value: "ca-central-1", label: "ca-central-1" },
        { value: "ca-west-1", label: "ca-west-1" },
        { value: "eu-central-1", label: "eu-central-1" },
        { value: "eu-west-1", label: "eu-west-1" },
        { value: "eu-west-2", label: "eu-west-2" },
        { value: "eu-south-1", label: "eu-south-1" },
        { value: "eu-west-3", label: "eu-west-3" },
        { value: "eu-south-2", label: "eu-south-2" },
        { value: "eu-north-1", label: "eu-north-1" },
        { value: "eu-central-2", label: "eu-central-2" },
        { value: "il-central-1", label: "il-central-1" },
        { value: "mx-central-1", label: "mx-central-1" },
        { value: "me-south-1", label: "me-south-1" },
        { value: "me-central-1", label: "me-central-1" },
        { value: "sa-east-1", label: "sa-east-1" },
        { value: "us-gov-east-1", label: "us-gov-east-1" },
        { value: "us-gov-west-1", label: "us-gov-west-1" },
    ],
    azure: [
        { value: "eastus", label: "eastus" },
        { value: "eastus2", label: "eastus2" },
        { value: "westus", label: "westus" },
        { value: "westus2", label: "westus2" },
        { value: "centralus", label: "centralus" },
        { value: "northcentralus", label: "northcentralus" },
        { value: "southcentralus", label: "southcentralus" },
        { value: "westcentralus", label: "westcentralus" },
        { value: "canadacentral", label: "canadacentral" },
        { value: "canadaeast", label: "canadaeast" },
        { value: "brazilsouth", label: "brazilsouth" },
        { value: "northeurope", label: "northeurope" },
        { value: "westeurope", label: "westeurope" },
        { value: "uksouth", label: "uksouth" },
        { value: "ukwest", label: "ukwest" },
        { value: "francecentral", label: "francecentral" },
        { value: "germanywestcentral", label: "germanywestcentral" },
        { value: "switzerlandnorth", label: "switzerlandnorth" },
        { value: "uaenorth", label: "uaenorth" },
        { value: "japaneast", label: "japaneast" },
        { value: "japanwest", label: "japanwest" },
        { value: "koreacentral", label: "koreacentral" },
        { value: "koreasouth", label: "koreasouth" },
        { value: "southeastasia", label: "southeastasia" },
        { value: "australiaeast", label: "australiaeast" },
    ],
};

// --- Validation Schema ---

const validationSchema = Yup.object().shape({
    application: Yup.string().required("Application is required"),
    cloudProvider: Yup.string()
        .required("This field is required")
        .oneOf(["gcp", "aws", "azure"], "Invalid cloud provider"),
    analyticsServiceType: Yup.string().when("cloudProvider", {
        is: (val: string) => val && analyticsServiceList[val as keyof typeof analyticsServiceList]?.length > 0,
        then: (schema) => schema.required("This field is required"),
    }),
    name: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-z0-9-_]*$/, "May only contain letters, numbers, dashes, and underscores")
        .max(30, "This field must not exceed 30 characters"),
    region: Yup.string().required("Region is required"),
    edition: Yup.string().required("Edition is required"),
    primaryDataSource: Yup.string().required("Primary Data Source is required"),
    costCenterValue: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(20, "This field must not exceed 20 characters"),
    costLabelPairs: Yup.array().of(
        Yup.object({
            cost: Yup.string()
                .required("This field is required")
                .strict()
                .lowercase("This field must be in lowercase")
                .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                .max(20, "This field must not exceed 20 characters")
                .notOneOf(["cost-center"], "This is a reserved key."),
            label: Yup.string()
                .required("This field is required")
                .strict()
                .lowercase("This field must be in lowercase")
                .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                .max(20, "This field must not exceed 20 characters"),
        })
    ),
});

// --- Interfaces ---
interface CostLabelPair {
    id?: string;
    cost: string;
    label: string;
}
interface DataBIFormValues {
    application: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | '';
    analyticsServiceType: string;
    name: string;
    region: string;
    edition: string;
    primaryDataSource: string;
    costLabelPairs: CostLabelPair[];
    costCenterValue: string;
}

interface DataBIFormProps {
    isEdit: boolean;
}
interface DataBIFormContentProps {
    isEdit: boolean;
}

const DataBIFormContent: React.FC<DataBIFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { id } = useParams<{ id: string }>();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    const [initialFormValues, setInitialFormValues] = useState<DataBIFormValues | null>(null);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [applications, setApplications] = useState<any[]>([]);
    const [applicationNamesList, setApplicationNamesList] = useState<string[]>([]);

    useEffect(() => {
        queryGraphql(`query { applications { id, applicationName, status } }`)
            .then(data => {
                if (data.applications) {
                    const activeApps = data.applications.filter((app: any) => app.status === 'active');
                    setApplications(activeApps);
                    setApplicationNamesList(Array.from(new Set(activeApps.map((app: any) => app.applicationName))) as string[]);
                }
            }).catch(err => console.error("Error fetching applications", err));
    }, []);

    useEffect(() => {
        if (isEdit && id) {
            setIsLoading(true);
            const getSingleAnalyticsQuery = `
                 query GetSolutionDataAnalytic($getDataAnalyticId: ID!) {
                      getDataAnalytic(id: $getDataAnalyticId) {
                    id
                    name
                    application { id, applicationName }
                    cloudProvider
                    solutionCategoryType
                    costLabelPairs {
                      cost
                      label
                    }
                    details {
                      dataAnalytics {
                        region
                        edition
                        primaryDataSource
                      }
                    }
                  }
                }
            `;
            queryGraphql(getSingleAnalyticsQuery, { getDataAnalyticId: id })
                .then(response => {
                    const da = response.getDataAnalytic;
                    if (da) {
                         const allPairs: { cost: string, label: string }[] = da.costLabelPairs || [];
                         const costCenter = allPairs.find(p => p.cost === "cost-center");
                         const dynamicPairs = allPairs
                            .filter(p => !["cost-center"].includes(p.cost))
                            .map((pair, index) => ({...pair, id: `clp-edit-${index}`}));

                        setInitialFormValues({
                            application: da.application?.applicationName || "",
                            cloudProvider: da.cloudProvider || 'gcp',
                            analyticsServiceType: da.solutionCategoryType || "",
                            name: da.name || "",
                            region: da.details?.dataAnalytics?.region || "",
                            edition: da.details?.dataAnalytics?.edition || "",
                            primaryDataSource: da.details?.dataAnalytics?.primaryDataSource || "",
                            costCenterValue: costCenter ? costCenter.label : "",
                            costLabelPairs: dynamicPairs,
                        });
                    } else {
                        console.error("Data Analytics service not found:", id);
                        navigate("/datasolutions/data-bi");
                    }
                })
                .catch(err => {
                    console.error("Error fetching Data Analytics service:", err);
                    navigate("/datasolutions/data-bi");
                })
                .finally(() => setIsLoading(false));
        } else {
            setInitialFormValues({
                application: "",
                cloudProvider: 'gcp',
                analyticsServiceType: 'looker',
                name: "",
                region: "asia-south1",
                edition: "core",
                primaryDataSource: "",
                costLabelPairs: [],
                costCenterValue: "",
            });
            setIsLoading(false);
        }
    }, [id, isEdit, navigate]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Data Analytics Service...</Typography>
            </Box>
        );
    }

    if (!initialFormValues) {
        return <Typography color="error" sx={{ p: 3 }}>Could not initialize form.</Typography>;
    }


    return (
        <Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/data-bi" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/data-bi"); }}>
                        Data Analytics & BI
                    </Link>
                    <Typography color="text.primary">
                        {isEdit ? "Edit Data Analytics & BI" : "Add New Data Analytics & BI"}
                    </Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-bi")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                        {isEdit ? "Edit Data Analytics & BI" : "Add New Data Analytics & BI"}
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                    <Formik
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                        enableReinitialize={true}
                        onSubmit={async (values, { setSubmitting, setStatus }) => {
                           setSubmitting(true);
                           const selectedApplication = applications.find(app => app.applicationName === values.application);
                           const combinedCostLabels = [
                               { cost: "cost-center", label: values.costCenterValue },
                               ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
                           ];

                           const apiPayload = {
                               name: values.name,
                               application: selectedApplication ? selectedApplication.id : undefined,
                               cloudProvider: values.cloudProvider,
                               solutionCategoryType: values.analyticsServiceType,
                               costLabelPairs: combinedCostLabels,
                               details: {
                                   dataAnalytics: {
                                       region: values.region,
                                       edition: values.edition,
                                       primaryDataSource: values.primaryDataSource
                                   }
                               }
                           };

                           let mutationString;
                           let variables: { input: any };
                           let mutationResultKey;

                           if (isEdit && id) {
                               mutationString = `mutation UpdateSolutionDataAnalytics($input: UpdateSolutionInput!) { updateSolutionDataAnalytics(input: $input) { id, name } }`;
                               variables = { input: { id, ...apiPayload } };
                               mutationResultKey = 'updateSolutionDataAnalytics';
                           } else {
                               mutationString = `mutation CreateSolutionDataAnalytics($input: CreateSolutionInput!) { createSolutionDataAnalytics(input: $input) { id, name } }`;
                               variables = { input: apiPayload };
                               mutationResultKey = 'createSolutionDataAnalytics';
                           }

                           try {
                                const responseData = await queryGraphql(mutationString, variables);
                                if (responseData[mutationResultKey]) {
                                    navigate("/datasolutions/data-bi");
                                } else {
                                    const errorMsg = responseData.errors ? JSON.stringify(responseData.errors) : 'API error.';
                                    setStatus({ error: `Failed to ${isEdit ? 'update' : 'create'} service. ${errorMsg}` });
                                }
                           } catch (error: any) {
                               setStatus({ error: `An unexpected error occurred: ${error.message}` });
                           }

                           setSubmitting(false);
                        }}
                    >
                        {({
                            handleChange,
                            handleBlur,
                            values,
                            setFieldValue,
                            isSubmitting,
                            isValid,
                            dirty,
                            touched,
                            errors
                        }) => (
                            <Form>
                                <Box>
                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>Provider & Service</Typography>
                                        <Divider sx={{ marginBottom: "24px" }} />
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} sm={12}>
                                                <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                               <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="cloudProvider" />
                                            </div>
                                            </Grid>
                                            <Grid item xs={12} sm={12}>
                                                <CompulsoryFieldsLabel fieldName="Analytics & BI Service" />
                                                <TextField select fullWidth name="analyticsServiceType" value={values.analyticsServiceType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.cloudProvider} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                    {values.cloudProvider && analyticsServiceList[values.cloudProvider as keyof typeof analyticsServiceList]?.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                                 <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="analyticsServiceType"/></div>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>Service Details</Typography>
                                        <Divider sx={{ marginBottom: "24px" }} />
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Name" />
                                                <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter service name" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name"/></div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Application" />
                                                <TextField select fullWidth name="application" value={values.application} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.application && Boolean(errors.application)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                    {applicationNamesList.map(appName => (<MenuItem key={appName} value={appName}>{appName}</MenuItem>))}
                                                </TextField>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                    <ErrorMessage name="application" />
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Region" />
                                                <TextField select fullWidth name="region" value={values.region} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.cloudProvider} error={touched.region && Boolean(errors.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                    {(regionList[values.cloudProvider as keyof typeof regionList] || []).map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="region"/></div>
                                            </Grid>
                                             <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Edition" />
                                                <TextField select fullWidth name="edition" value={values.edition} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.cloudProvider} error={touched.edition && Boolean(errors.edition)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                    {(editionList[values.cloudProvider as keyof typeof editionList] || []).map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="edition"/></div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Primary Data Source" />
                                                <TextField fullWidth name="primaryDataSource" value={values.primaryDataSource} onChange={handleChange} onBlur={handleBlur} placeholder="Enter primary data source" variant="filled" hiddenLabel error={touched.primaryDataSource && Boolean(errors.primaryDataSource)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}/>
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="primaryDataSource"/></div>
                                            </Grid>
                                        </Grid>
                                    </div>
                                    
                                    <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cost Labels</Typography>
                                        <Divider sx={{ mb: "24px" }} />
                                        <Grid container spacing={3} sx={{ mb: "16px" }}>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Key" />                                       
                                                <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }}/>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CompulsoryFieldsLabel fieldName="Value" />
                                                <TextField fullWidth placeholder="Enter value" value={values.costCenterValue} name="costCenterValue" onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
                                            </Grid>
                                        </Grid>
                                         <FieldArray
                                            name="costLabelPairs"
                                            render={arrayHelpers => (
                                                <div>
                                                    {values.costLabelPairs.map((pair, index) => (
                                                        <Grid container spacing={2} key={pair.id || index} sx={{ mb: "16px" }} alignItems="center">
                                                            <Grid item xs={5}>
                                                                <CompulsoryFieldsLabel fieldName="Key" />
                                                                <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                                <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <CompulsoryFieldsLabel fieldName="Value" />
                                                                <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                                <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
                                                            </Grid>
                                                            <Grid item xs={1}><IconButton onClick={() => arrayHelpers.remove(index)} sx={{ mt: 2 }}><img alt="remove" src="/assets/close-icon.svg" /></IconButton></Grid>
                                                        </Grid>
                                                    ))}
                                                    <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <FormControlLabel
                                        control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }} />}
                                        label={<Typography sx={{ color: 'black' }}>I confirm that the details provided are correct and I wish to proceed.</Typography>}
                                        sx={{ mt: 2.5, display: 'flex' }}
                                    />
                                    <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                                        <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-analytics")}>Cancel</Button>
                                        <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4 }} variant="contained" size="large" type="submit" disabled={isSubmitting || !isValid || !dirty || !confirmationChecked || isViewer}>
                                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New BI Tool")}
                                        </Button>
                                    </Box>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

const DataBIForm: React.FC<DataBIFormProps> = ({ isEdit }) => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"datasolutions-data-analytics"}
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <DataBIFormContent isEdit={isEdit} />
            </Box>
        </Box>
    );
};

export default DataBIForm;